import * as Yup from "yup";
import { Form, Formik, FormikProps } from "formik";
import { useRef, useState } from "react";
import { PolicySavePayloadDTO } from "../../types";
import { Box, Divider, Grid, Typography, useTheme } from "@mui/material";
import FormikDropdown from "../../components/Input/FormikDropdown";
import MyText from "../../components/Input/MyText";
import { LoadingButton } from "@mui/lab";
import { useStateValue } from "../../util/stateProvider";
import { FormValues, getValidationSchema } from "./health-policy-form";
import { policyService } from "../../services/api/policyServics";
import dayjs from "dayjs";

type Props = {
    onSubmit: (payload: PolicySavePayloadDTO) => void;
    policyData: PolicySavePayloadDTO | null;
    isLoading: boolean;
    customer: {
        label: string;
        value: string;
    };
};

const LifePolicyOptions = [
    {
        id: "ENDOWNMENT",
        name: "ENDOWNMENT",
    },
    {
        id: "PENSION",
        name: "PENSION",
    },
    {
        id: "SAVINGS",
        name: "SAVINGS",
    },
    {
        id: "TERM",
        name: "TERM",
    },
    {
        id: "Term ROP",
        name: "TERM_ROP",
    },
    {
        id: "ULIP",
        name: "ULIP",
    },
];

export default function TermPolicyForm({
    onSubmit,
    policyData,
    isLoading,
    customer,
}: Props) {
    const formikRef = useRef<FormikProps<FormValues>>(null);
    const theme = useTheme();
    const [{ user }] = useStateValue();
    let agentUuid = user?.agentUuid || null;
    const [isFetched, setIsFetched] = useState({
        insurer: false,
    });
    const [insurerOptions, setInsurerOptions] = useState<
        { id: string; name: string }[]
    >([]);

    const fetchInsurerOptions = () => {
        if (agentUuid && !isFetched.insurer) {
            policyService.getInsurerList(agentUuid).then((res) => {
                if (res && res.data.response.length) {
                    setInsurerOptions(
                        res.data.response.map((insurer) => ({
                            id: insurer.value,
                            name: insurer.label,
                        }))
                    );
                    setIsFetched((prev) => ({ ...prev, insurer: true }));
                }
            });
        }
    };

    const handleSubmit = (values: FormValues) => {
        if (customer && customer?.value && values.policyType) {
            let payload: PolicySavePayloadDTO = {
                ...policyData,
                client: {
                    label: customer?.label,
                    value: customer?.value,
                },
                flow: 3,
                businessType: 1,
                insuranceCategory: values.insuranceCategory,
                policyType: values.policyType,
                insurer: values.insurer,
                policyId: values.policyId,
                startDate: values.startDate
                    ? dayjs(values.startDate).format("YYYY-MM-DD")
                    : "",
                endDate: values.endDate
                    ? dayjs(values.endDate).format("YYYY-MM-DD")
                    : values.startDate
                    ? dayjs(values.startDate)
                          .add(values.coverPeriod || 0, "year")
                          .format("YYYY-MM-DD")
                    : "",
                coverPeriod: values.coverPeriod,
                netPremium: values.netPremium || 0,
                gstPremium: values.gstPremium || 0,
                policyPremium: values.policyPremium || 0,
                si: values.si || 0,
                type: "policy",
            };
            onSubmit(payload);
        }
    };
    return (
        <>
            <Formik
                // @ts-ignore
                ref={formikRef}
                initialValues={{
                    insuranceCategory: "Life",
                    policyType: policyData?.policyType
                        ? LifePolicyOptions.find(
                              (p) => p.id === policyData?.policyType
                          )?.id
                        : undefined,
                    insurer: policyData?.insurer,
                    startDate: policyData?.startDate
                        ? new Date(policyData?.startDate)
                        : null,
                    endDate: policyData?.endDate
                        ? new Date(policyData?.endDate)
                        : null,
                    coverPeriod: policyData?.coverPeriod,
                    policyId: policyData?.policyId,
                    netPremium: policyData?.netPremium,
                    gstPremium: policyData?.gstPremium,
                    policyPremium: policyData?.policyPremium,
                    si: policyData?.si || policyData?.sumInsured || 0,
                }}
                validationSchema={getValidationSchema().shape({
                    coverPeriod: Yup.number().required(),
                    endDate: Yup.date().nullable(),
                })}
                onSubmit={(values) => {
                    console.log("Form submitted with values:", values);
                    values = {
                        ...values,
                        endDate: dayjs(values.startDate)
                            .add(values.coverPeriod || 0, "year")
                            .toDate(),
                    };

                    handleSubmit(values);
                }}
            >
                {({ isSubmitting, errors }) => {
                    console.log(errors);

                    return (
                        <>
                            <Form>
                                <Box
                                    sx={{
                                        minHeight: { xs: "100%", sm: "456px" },
                                    }}
                                >
                                    <Grid container spacing={3}>
                                        <Grid item xs={12}>
                                            <Typography variant="subtitle1">
                                                Basic policy details
                                            </Typography>
                                        </Grid>
                                        <Grid item xs={12} sm={6}>
                                            <FormikDropdown
                                                title="Insurance Category"
                                                name="insuranceCategory"
                                                className=" dropDownSuper colorUpdate"
                                                defaultVal={"Life"}
                                                items={[
                                                    {
                                                        id: "Life",
                                                        name: "Life",
                                                    },
                                                ]}
                                                change={() => {}}
                                                disabled
                                                readOnly
                                            />
                                        </Grid>
                                        <Grid item xs={12} sm={6}>
                                            <FormikDropdown
                                                title="Policy Type"
                                                name="policyType"
                                                className=" dropDownSuper colorUpdate"
                                                defaultVal={
                                                    policyData?.policyType
                                                }
                                                items={LifePolicyOptions}
                                                change={() => {}}
                                            />
                                        </Grid>
                                        <Grid item xs={12} sm={6}>
                                            <FormikDropdown
                                                title="Insurer"
                                                name="insurer"
                                                className=" dropDownSuper colorUpdate"
                                                defaultVal={policyData?.insurer}
                                                items={
                                                    isFetched.insurer
                                                        ? insurerOptions
                                                        : policyData?.insurer
                                                        ? [
                                                              {
                                                                  id: policyData?.insurer,
                                                                  name: policyData?.insurer,
                                                              },
                                                          ]
                                                        : []
                                                }
                                                change={() => {}}
                                                onOpen={() => {
                                                    fetchInsurerOptions();
                                                }}
                                            />
                                        </Grid>
                                        <Grid item xs={12} sm={6}>
                                            <MyText
                                                label="Start date"
                                                name="startDate"
                                                type="date"
                                                onChange={() => {}}
                                            />
                                        </Grid>
                                        <Grid item xs={12} sm={6}>
                                            <MyText
                                                label="Policy term coverage"
                                                name="coverPeriod"
                                                type="text"
                                                onChange={() => {}}
                                            />
                                        </Grid>
                                        <Grid item xs={12} sm={6}>
                                            <MyText
                                                label="Policy number"
                                                name="policyId"
                                                type="text"
                                                className=""
                                                onChange={() => {}}
                                            />
                                        </Grid>
                                        <Grid item xs={12} sm={6}>
                                            <MyText
                                                label="Sum insured"
                                                name="si"
                                                type="text"
                                                className=""
                                                onChange={() => {}}
                                            />
                                        </Grid>
                                        <Grid item xs={12}>
                                            <Typography variant="subtitle1">
                                                Premium details
                                            </Typography>
                                        </Grid>
                                        <Grid item xs={12} sm={6}>
                                            <MyText
                                                label="Net premium *"
                                                name="netPremium"
                                                type="text"
                                                className=""
                                                onChange={() => {}}
                                            />
                                        </Grid>
                                        <Grid item xs={12} sm={6}>
                                            <MyText
                                                label="GST amount"
                                                name="gstPremium"
                                                type="text"
                                                className=""
                                                onChange={() => {}}
                                            />
                                        </Grid>
                                        <Grid item xs={12} sm={6}>
                                            <MyText
                                                label="Total premium"
                                                name="policyPremium"
                                                type="text"
                                                className=""
                                                onChange={() => {}}
                                            />
                                        </Grid>
                                    </Grid>
                                </Box>
                                <Divider
                                    variant="dashed"
                                    sx={{
                                        borderColor: "grey.400",
                                        opacity: 0.3,
                                        my: "16px",
                                    }}
                                />
                                <Box
                                    sx={{
                                        display: "flex",
                                        justifyContent: "flex-end",
                                        alignItems: "center",
                                        flexDirection: {
                                            xs: "column",
                                            sm: "row",
                                        },
                                    }}
                                >
                                    <LoadingButton
                                        size="small"
                                        variant="contained"
                                        sx={{
                                            marginLeft: { xs: 0, sm: "auot" },
                                            alignSelf: {
                                                xs: "center",
                                                sm: "flex-end",
                                            },
                                            background:
                                                theme.palette.brand.main,
                                            width: { xs: "100%", sm: "auto" },
                                        }}
                                        loading={isLoading}
                                        disabled={isSubmitting}
                                        type="submit"
                                    >
                                        Submit
                                    </LoadingButton>
                                </Box>
                            </Form>
                        </>
                    );
                }}
            </Formik>
        </>
    );
}
